import React, { useMemo, useRef } from 'react'
import { TooltipProps } from 'react-bootstrap/Tooltip'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { OverlayChildren } from 'react-bootstrap/Overlay'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { useNavigate } from 'react-router-dom'
import PrizeBadge from '../../../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { parseMetadata } from '../utils'
import { SentimentAnalysisMetadata } from '../SentimentAnalysis/model'
import { useZocksAuth, useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import './ConfigureAssignment.scss'
import { useAccount } from '../../../../../common/useAccount'
import { MeetingLanguage } from '../../../../../models/Room'

const CustomSentimentQuotaTooltip = React.forwardRef<HTMLDivElement, any>(({ accessCheckResult: { quota, scope }, className, ...props }, ref) => {
  const navigate = useNavigate()

  const tooltipClass = `upgrade-tooltip ${className || ''}`

  const renderWithAction = () => (
    <Tooltip id='overlay-example' ref={ref} {...props} className={tooltipClass}>
      <div className='d-flex flex-column zocks-gap-1.25'>
        <div className='text-nowrap'>
          <img alt='Upgrade' src={PrizeBadge} />
          <b>Upgrade to unlock this feature</b>
        </div>
        <Button variant='primary' className='py-1' onClick={() => navigate('/account/plan')}>
          Get feature
        </Button>
      </div>
    </Tooltip>
  )

  const renderNoScope = (message: string) => (
    <Tooltip id='overlay-example' ref={ref} {...props} className={tooltipClass}>
      <div className='d-flex flex-column zocks-gap-1.25'>
        <div className='text-nowrap'>
          <b>This feature is currently unavailable.</b>
        </div>
        <div>{message}</div>
      </div>
    </Tooltip>
  )

  if (!quota && scope) {
    return renderWithAction()
  }

  if (!quota && !scope) {
    return renderNoScope('Please contact your admin to upgrade your subscription.')
  }

  return null
})

export type SentimentAnalysisToggleButtonProps = {
  showSentimentAnalysis: boolean
  onToggleSentimentAnalysis: () => void
}

const ViewParticipantSentimentButton = ({ onClick, disabled }: { onClick?: () => void; disabled?: boolean }) => (
  <Button variant='grayDarkGhostButton' className='p-2' onClick={onClick} disabled={disabled}>
    <i className='bi bi-chat-heart' /> <span className='button-medium'>View participant sentiment</span>
  </Button>
)

export const SentimentAnalysisToggleButton: React.FC<SentimentAnalysisToggleButtonProps> = ({ showSentimentAnalysis, onToggleSentimentAnalysis }) => {
  const tooltipRef = useRef<HTMLDivElement>(null)
  const { hasQuota } = useZocksQuota()
  const sentimentAnalysisEnabled = hasQuota('ai.sentimentAnalysis.value')
  const { hasScope } = useZocksAuth()
  const navigate = useNavigate()
  const { account } = useAccount({ refetchOnMountOrArgChange: true })
  const roomSession = useAppSelector(selectRoomSession)

  const hasSentimentAnalysisData = useMemo(() => {
    if (!roomSession) {
      return false
    }

    const metadata = parseMetadata(roomSession.metadata) as SentimentAnalysisMetadata
    const hasData = metadata?.sentimentAnalysis?.overall ?? false
    if (!hasData) {
      return false
    }
    return hasData
  }, [roomSession])

  const NoSentimentTooltip = React.forwardRef((props: TooltipProps, ref: React.Ref<HTMLDivElement>) => (
    <Tooltip id='button-tooltip' ref={ref} {...props}>
      This feature is not available for this meeting. Our system only processes meetings from the point of activation forward, not retroactively.
    </Tooltip>
  ))

  const NonEnglishTooltip = React.forwardRef((props: TooltipProps, ref: React.Ref<HTMLDivElement>) => (
    <Tooltip id='non-english-tooltip' ref={ref} {...props}>
      Sentiment analysis is only available for English meetings.
    </Tooltip>
  ))

  const meetingLanguage = roomSession?.config?.language?.language
  const isNonEnglishMeeting = meetingLanguage && meetingLanguage !== MeetingLanguage.ENGLISH

  const AdminToolTip = React.forwardRef((props: TooltipProps, ref: React.Ref<HTMLDivElement>) => (
    <Tooltip id='admin-tooltip' ref={ref} {...props} className={`upgrade-tooltip ${props.className || ''}`}>
      <div className='d-flex flex-column zocks-gap-1.25'>
        <div className='text-nowrap'>
          <b>This feature is currently unavailable.</b>
        </div>
        <Button
          variant={'primary'}
          className='py-1'
          onClick={() => {
            navigate('/workspace/setup/settings')
          }}
        >
          Go to Settings
        </Button>
      </div>
    </Tooltip>
  ))

  const UserTooltip = React.forwardRef((props: TooltipProps, ref: React.Ref<HTMLDivElement>) => (
    <Tooltip id='user-tooltip' ref={ref} {...props} className={`upgrade-tooltip ${props.className || ''}`}>
      <div className='d-flex flex-column zocks-gap-1.25'>
        <div className='text-nowrap'>
          <b>This feature is currently unavailable.</b>
        </div>
        <div>Please contact your admin to enable access.</div>
      </div>
    </Tooltip>
  ))

  const SentimentOverlayButton = ({ overlay }: { overlay: OverlayChildren }) => (
    <OverlayTrigger trigger='click' overlay={overlay}>
      <div>
        <ViewParticipantSentimentButton />
      </div>
    </OverlayTrigger>
  )

  const isAccountAdmin = hasScope('**', 'account:update', ['account:admin'])
  const shouldShowScopedBlock = !sentimentAnalysisEnabled
  const isSentimentDisabled = !account?.effectiveConfig.sentimentAnalysis

  let content: React.ReactNode

  if (shouldShowScopedBlock) {
    content = (
      <ScopedBlock
        missingScopeAction={MissingScopeAction.UPGRADE}
        scope={{ scope: 'account:update', superScope: ['account:admin'] }}
        tooltipElement={<CustomSentimentQuotaTooltip ref={tooltipRef} />}
        quota={{ quota: 'ai.sentimentAnalysis.value' }}
        showUpgradeBadge={false}
      >
        <ViewParticipantSentimentButton onClick={onToggleSentimentAnalysis} />
      </ScopedBlock>
    )
  } else if (isSentimentDisabled && isAccountAdmin) {
    content = <SentimentOverlayButton overlay={<AdminToolTip />} />
  } else if (isSentimentDisabled && !isAccountAdmin) {
    content = <SentimentOverlayButton overlay={<UserTooltip />} />
  } else if (isNonEnglishMeeting) {
    content = (
      <OverlayTrigger placement='top' overlay={<NonEnglishTooltip />}>
        <div>
          <ViewParticipantSentimentButton disabled />
        </div>
      </OverlayTrigger>
    )
  } else if (!hasSentimentAnalysisData) {
    content = <SentimentOverlayButton overlay={<NoSentimentTooltip />} />
  } else {
    content = <ViewParticipantSentimentButton onClick={onToggleSentimentAnalysis} />
  }

  return <div>{content}</div>
}
